import { IntlShape } from "react-intl";
import { getDateText, DateFormat, getDestinationTimeZone } from "../../../helpers/dateHelper";
import Messages from "../../../localization/Messages";
import { IBooking, getBookingStatusMessageDescriptor } from "../../../models/booking";
import { IDock } from "../../../models/dock";

export const bookingCsvColumnDefs = [
    Messages.bookingNumber,
    Messages.shipName,
    Messages.eni,
    Messages.berthings,
    Messages.assignedDock,
    Messages.arrivalTime,
    Messages.departureTime,
    Messages.managingCompany,
    Messages.owningCompany,
    Messages.bookingStatus,
    Messages.bookingFinalized,
    Messages.dispatcherRemark,
    Messages.sharedRemark,
    Messages.completeness,
];

const sanitizeValues = (values: any[]): string[] => {
    return values.map(value => {
        if (value === null || value === undefined) return "";

        const str = String(value);

        return str
            .replace(/"/g, '""') // Escape quotes
            .replace(/\r?\n/g, "\r\n"); // Normalize newlines to Windows-style
    });
};

const bookingCsvMapper = (
    booking: IBooking,
    intl: IntlShape,
    managingCompanies: Map<any, any>,
    docks: IDock[],
    timeZones: ReadonlyMap<string, string> = new Map(),
): string[][] => {
    const timeZone = getDestinationTimeZone(timeZones.get(booking.destinationId));
    const bookingRows: string[][] = [];
    if (booking?.berthings?.length && booking.berthings.length > 0) {
        booking.berthings.forEach(berthing => {
            const entry = [
                booking.id,
                booking.shipName,
                booking.ship?.eni,
                booking?.berthings?.length,
                docks.find(dock => dock.id === berthing.dockId)?.name ?? "",
                getDateText(berthing.arrivalTime, DateFormat.CLIENT_DATE_TIME, timeZone),
                getDateText(berthing.departureTime, DateFormat.CLIENT_DATE_TIME, timeZone),
                managingCompanies.get(booking.managingCompany) ?? "",
                booking.owningCompany ?? "",
                intl.formatMessage(getBookingStatusMessageDescriptor(booking)),
                intl.formatMessage(booking.finalized ? Messages.yes : Messages.no),
                booking.comment ?? "",
                booking.sharedRemark ?? "",
                intl.formatMessage(booking.complete ? Messages.yes : Messages.no),
            ];
            bookingRows.push(sanitizeValues(entry));
        });
    } else {
        const entry = [
            booking.id,
            booking.ship?.name ?? "",
            booking.ship?.eni,
            0,
            docks.find(dock => dock.id === booking.dockId)?.name ?? "",
            getDateText(
                booking.actualArrivalTime ?? booking.arrivalTime,
                DateFormat.CLIENT_DATE_TIME, timeZone
            ),
            getDateText(
                booking.actualDepartureTime ?? booking.departureTime,
                DateFormat.CLIENT_DATE_TIME, timeZone
            ),
            managingCompanies.get(booking.managingCompany) ?? "",
            booking.owningCompany ?? "",
            intl.formatMessage(getBookingStatusMessageDescriptor(booking)),
            intl.formatMessage(booking.finalized ? Messages.yes : Messages.no),
            booking.comment ?? "",
            booking.sharedRemark ?? "",
            intl.formatMessage(booking.complete ? Messages.yes : Messages.no),
        ];
        bookingRows.push(sanitizeValues(entry));
    }
    return bookingRows;
};

export const getBookingCsvFields = (
    bookings: IBooking[],
    intl: IntlShape,
    managingCompanies: Map<any, any>,
    docks: IDock[],
    timeZones: ReadonlyMap<string, string> = new Map(),
): string[][] => {
    const rows: string[][] = [];

    bookings.forEach(booking => {
        const bookingRows = bookingCsvMapper(booking, intl, managingCompanies, docks, timeZones);
        rows.push(...bookingRows);
    });

    return rows;
};
